import { Box, Button, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import React from "react";
import { useNavigate } from "react-router-dom";

const tokens = {
  ink: "#12242E",
  teal: "#0F8B8D",
  tealDark: "#0A5F60",
  line: "rgba(18,36,46,0.10)",
  headingFont: "'Sora', sans-serif",
};

// Validates/fixes a (possibly malformed) image URL. Returns null if it
// can't be turned into something usable — the caller then shows the
// "Image is Private" fallback instead of a broken <img>.
const getSafeImageUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  let url = rawUrl.trim();
  if (!url) return null;

  if (url.startsWith("//")) {
    url = "https:" + url;
  } else if (!/^https?:\/\//i.test(url)) {
    url = "https://" + url.replace(/^\/+/, "");
  }

  const match = url.match(/^(https?:\/\/)(.*)$/i);
  if (!match) return null;
  const [, protocol, rest] = match;
  const finalUrl = protocol + rest.replace(/\/{2,}/g, "/");

  try {
    // eslint-disable-next-line no-new
    new URL(finalUrl);
    return finalUrl;
  } catch (e) {
    return null;
  }
};

// Shown instead of a broken/inaccessible image, in a box that matches the
// same aspect ratio as a real photo so the layout never jumps.
const PrivateImageBanner = () => (
  <Box
    sx={{
      width: "100%",
      aspectRatio: "4 / 3",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      bgcolor: "#EEF2F2",
      borderRadius: "20px",
      color: "rgba(18,36,46,0.45)",
    }}
  >
    <LockRoundedIcon sx={{ fontSize: "22pt" }} />
    <Typography sx={{ fontSize: "10pt", fontWeight: 600 }}>
      Image is Private
    </Typography>
  </Box>
);

const FreelancerCommentItem = (props) => {
  const navigate = useNavigate();
  const { data, order } = props;
  const { banner_pic, title, info, subtitle, freelancer_id } = data;
  const imageUrl = getSafeImageUrl(banner_pic);

  // Only the image alternates sides (order 0 = left, order 1 = right, via
  // row-reverse). The text column always stays left-aligned within itself,
  // whichever side it lands on — mixing text alignment with column order
  // is what caused text to crowd into the middle next to the image.
  const isImageOnRight = order === 1;

  return (
    <Box
      className="freelancer-card"
      sx={{
        width: "100%",
        border: `1px solid ${tokens.line}`,
        borderRadius: "24px",
        p: { xs: 2.5, md: 4 },
        bgcolor: "white",
        transition: "box-shadow 0.25s ease, transform 0.25s ease",
        "&:hover": {
          boxShadow: "0 20px 40px rgba(18,36,46,0.10)",
          transform: "translateY(-3px)",
        },
        "&:hover .freelancer-photo": {
          transform: "scale(1.03)",
        },
      }}
    >
      <Stack
        direction={{
          xs: "column",
          md: isImageOnRight ? "row-reverse" : "row",
        }}
        alignItems="center"
        spacing={{ xs: 3, md: 5 }}
      >
        <Box sx={{ width: { xs: "100%", md: "42%" }, overflow: "hidden", borderRadius: "20px" }}>
          {imageUrl ? (
            <Box
              className="freelancer-photo"
              component="img"
              alt={title}
              src={imageUrl}
              sx={{
                width: "100%",
                aspectRatio: "4 / 3",
                objectFit: "cover",
                borderRadius: "20px",
                boxShadow: "0 16px 32px rgba(18,36,46,0.12)",
                transition: "transform 0.35s ease",
                display: "block",
              }}
            />
          ) : (
            <PrivateImageBanner />
          )}
        </Box>

        <Stack
          spacing={1.25}
          alignItems="flex-start"
          sx={{ width: { xs: "100%", md: "58%" } }}
        >
          <FormatQuoteRoundedIcon
            sx={{ fontSize: "22pt", color: `${tokens.teal}55` }}
          />
          <Typography
            sx={{
              fontFamily: tokens.headingFont,
              fontWeight: 700,
              fontSize: { xs: "15pt", md: "19pt" },
              color: tokens.ink,
              textAlign: "left",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: "10.5pt", md: "12pt" },
                color: tokens.teal,
                textAlign: "left",
              }}
            >
              {subtitle}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: { xs: "10pt", md: "11pt" },
              lineHeight: 1.6,
              color: "rgba(18,36,46,0.7)",
              textAlign: "left",
              maxWidth: 460,
            }}
          >
            {info}
          </Typography>
          <Button
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => navigate("/profiledetails/" + freelancer_id)}
            size="small"
            sx={{
              mt: 1,
              borderRadius: "30px",
              bgcolor: tokens.teal,
              textTransform: "capitalize",
              fontWeight: 600,
              px: 2.5,
              transition: "background-color 0.2s ease, transform 0.15s ease",
              "&:hover": { bgcolor: tokens.tealDark, transform: "translateY(-2px)" },
            }}
          >
            Read more
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default FreelancerCommentItem;